import { Link } from "react-router-dom";
import { RingBadge } from "@/components/ui/RingBadge";
import type { Practice, Ring, Tool } from "@/api/types";

// The reverse of a tool's Satisfies / Supports links: on a standard's or capability's page,
// the tools and practices linked to it. Confirmed links are what put an entry in a map's
// "Required by a standard" / "Used to deliver this capability"; drafted ones still await a
// person, on the tool's page.

const HEADING = "mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase";

interface Entry {
  type: "tool" | "practice";
  id: string;
  name: string;
  ring: Ring | null;
  reason?: string;
}

/** The catalog entries linked to one standard or capability: confirmed first, then drafted
 * (with the model's reason), each linking to its page. */
function EntryList({ entries }: { entries: Entry[] }) {
  return (
    <ul className="flex flex-col gap-1 text-sm">
      {entries.map((e) => (
        <li key={`${e.type}:${e.id}`} className="flex flex-wrap items-center gap-2">
          <Link
            to={`/${e.type === "tool" ? "tools" : "practices"}/${encodeURIComponent(e.id)}`}
            className="text-accent underline"
          >
            {e.name}
          </Link>
          {e.ring && <RingBadge ring={e.ring} />}
          {e.type === "practice" && <span className="text-xs text-text/50">practice</span>}
          {e.reason && <span className="w-full text-text/60">{e.reason}</span>}
        </li>
      ))}
    </ul>
  );
}

/** Tools and practices linked to the standard or capability `id`. `link` says which kind of
 * link to read: a standard's are tools' satisfies_standards and practices' related_standards;
 * a capability's are tools' supports_capabilities. Deprecated entries are left out. */
export function LinkedFromCatalog({
  id,
  link,
  tools,
  practices = [],
}: {
  id: string;
  link: "standard" | "capability";
  tools: Tool[];
  practices?: Practice[];
}) {
  const active = <T extends { status: string }>(xs: T[]) => xs.filter((x) => x.status === "active");
  const byName = (a: Entry, b: Entry) => a.name.localeCompare(b.name);
  const entry = (type: Entry["type"], x: Tool | Practice, reason?: string): Entry => ({
    type, id: x.id, name: x.name, ring: x.ring, reason,
  }); // prettier-ignore

  const confirmed: Entry[] = [
    ...active(tools)
      .filter((t) => (link === "standard" ? t.satisfies_standards : t.supports_capabilities).includes(id))
      .map((t) => entry("tool", t)),
    ...(link === "standard" ? active(practices).filter((p) => p.related_standards.includes(id)) : []).map((p) =>
      entry("practice", p),
    ),
  ].sort(byName);
  const drafted: Entry[] = [
    ...active(tools).flatMap((t) =>
      (link === "standard" ? t.drafted_standards : t.drafted_capabilities)
        .filter((d) => d.id === id)
        .map((d) => entry("tool", t, d.reason)),
    ),
    ...(link === "standard" ? active(practices) : []).flatMap((p) =>
      p.drafted_standards.filter((d) => d.id === id).map((d) => entry("practice", p, d.reason)),
    ),
  ].sort(byName);

  return (
    <section className="flex flex-col gap-2">
      <div>
        <h4 className={HEADING}>
          {link === "standard" ? "Tools and practices that meet it" : "Tools that support it"}
        </h4>
        {confirmed.length === 0 ? (
          <p className="text-sm text-text/50">None confirmed yet.</p>
        ) : (
          <EntryList entries={confirmed} />
        )}
      </div>
      {drafted.length > 0 && (
        <div>
          <h4 className={HEADING}>Drafted links — awaiting review</h4>
          <p className="mb-1 text-xs text-text/50">
            Proposed by the model; confirm or dismiss them on each entry's page.
          </p>
          <EntryList entries={drafted} />
        </div>
      )}
    </section>
  );
}
